import { onMounted, onBeforeUnmount } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { getSocket } from "@/utils/socket";
import type { MessageReceivedPayload } from "@/dtos/MessageWebhook.dto";
import type { ContactEngagementSummary } from "@/dtos/Contact.dto";

type InboundChannel = "whatsapp" | "linkedin" | "sms";

/**
 * Keeps unread-message counts live across the app (sidebar badges, dashboard bucket
 * cards, buckets overview cards, and the buckets table Activity column) by reacting to
 * the same inbound-message socket events the chat panels already use.
 *
 * - Per-contact counts (`engagement-summary`) are bumped by exactly 1 in place, with
 *   no network round-trip, since the payload always carries `contactId`.
 * - Per-bucket counts (`dashboardStats`) can't be bumped locally: the payload has no
 *   way to say which bucket the contact is currently in, so instead this triggers an
 *   immediate scoped refetch of just that query the moment the event arrives — still
 *   feels live since it's event-driven, not a manual refresh or a polling interval.
 *
 * Mount this once, globally (in App.vue), so it works regardless of which page is open.
 */
export function useUnreadCountSync() {
  const queryClient = useQueryClient();
  let socket: ReturnType<typeof getSocket> | null = null;

  const bumpEngagementSummary = (contactId: string, channel: InboundChannel) => {
    queryClient.setQueriesData<Record<string, ContactEngagementSummary>>(
      { queryKey: ["engagement-summary"], exact: false },
      (old) => {
        const current = old?.[contactId];
        if (!current) return old;
        return {
          ...old,
          [contactId]: {
            ...current,
            unread: {
              ...current.unread,
              [channel]: (current.unread[channel] || 0) + 1,
              total: (current.unread.total || 0) + 1,
            },
          },
        };
      }
    );
  };

  const bumpDashboardStats = () => {
    queryClient.refetchQueries({ queryKey: ["dashboardStats"], exact: false, type: "active" });
  };

  const handleUnipileMessage = (data: MessageReceivedPayload) => {
    const payload = data?.payload;
    if (!payload?.contactId) return;
    if (payload.channel !== "whatsapp" && payload.channel !== "linkedin") return;
    bumpEngagementSummary(payload.contactId, payload.channel);
    bumpDashboardStats();
  };

  const handleSMSReceived = (data: any) => {
    if (!data?.contactId) return;
    bumpEngagementSummary(data.contactId, "sms");
    bumpDashboardStats();
  };

  onMounted(() => {
    socket = getSocket();
    socket.on("unipile:message:received", handleUnipileMessage);
    socket.on("sms:received", handleSMSReceived);
  });

  onBeforeUnmount(() => {
    if (socket) {
      socket.off("unipile:message:received", handleUnipileMessage);
      socket.off("sms:received", handleSMSReceived);
    }
  });
}
